import { HydraCollection, IRI } from 'emt-library'
import { UserCardAnimal } from '@/dataV3/Search/UserCard'

export const cancellationPoliciesMapping = [
  {
    slug: 'fullRefundUpToServiceStartTime',
    title: 'Jusqu’au début du service',
  },
  {
    slug: 'fullRefundUpToTheDayBeforeTheService',
    title: "Jusqu'à 24h avant",
  },
  {
    slug: 'fullRefundUpTo3DaysBeforeService',
    title: "Jusqu'à 3 jours avant",
  },
  {
    slug: 'fullRefundUpTo7DaysBeforeService',
    title: "Jusqu'à 7 jours avant",
  },
]

export interface ConversationsData {
  '@context': string
  '@id': string
  '@type': string
  'hydra:totalItems': number
  'hydra:member': PetsitterMessageSummary[]
}

export interface PetsitterMessageSummary {
  '@id': string
  '@type': string
  conversationUuid: string
  header: PetsittingMessageSummaryHeader
  authorAvatarUrl: string
  title: string
  lastMessageContent: string
  lastMessageDate: string
  status: string
  unreadMessageCount: number
  profiles: any[]
}

export interface PetsittingMessageSummaryHeader {
  '@type': string
  '@id': string
  title: string
  subtitle: string
  icon: string
  status: string
  periods: Period[]
}

/**
 * A pet owner's request sent to multiple pet sitters at once (not yet a conversation).
 */
export interface RequestCartProduct {
  '@type': string
  '@id': string
  offer: string
  start: string
  end: string
  isAdded: boolean
}

export interface RequestCart {
  '@type': string
  '@id': string
  requestId: string
  animals: UserCardAnimal[]
  petsitters: string[]
  products: RequestCartProduct[]
  message: string
  quotesIds: string[]
  submittedAt: string
  latitude?: number
  longitude?: number
  joinCount?: number
  maxDistance?: number
}

export interface RequestCartsData {
  '@context': string
  '@id': string
  '@type': string
  'hydra:totalItems': number
  'hydra:member': RequestCart[]
}

/**
 * Top-level interface for the entire conversation response.
 */
export interface Conversation {
  '@context': string
  '@id': string
  '@type': string
  uuid: string
  reservationUuid: string
  creationDate: string
  conversationItems: ConversationItems
  transactionSummary: TransactionSummary
  conversationHeader: ConversationHeader
  header: PetsittingMessageSummaryHeader
  conversationUuid: string
}

/**
 * Container interface for conversation items (messages + notifications).
 */
export interface ConversationItems {
  '@context': string
  '@id': string
  '@type': string
  'hydra:member': Array<ConversationMessage | ConversationNotification>
  'hydra:totalItems': number
}

/**
 * Represents a single conversation message.
 */
export interface ConversationMessage {
  '@type': 'ConversationMessage'
  '@id': string
  uuid: string
  authorAvatarUrl: string
  content: string
  authorUuid: string
  createdAt: string
  isFromCurrentUser: boolean

  repliedToUuid?: string
  repliedToSummary?: string
  repliedToDate?: string

  reactions: Reaction[]
}

/**
 * Represents a single conversation notification (e.g., "Marie-Christine vous a envoyé une demande").
 */
export interface ConversationNotification {
  '@type': 'ConversationNotification'
  '@id': string
  uuid: string
  title: string
  subtitle?: string // Not all notifications have a subtitle
  actions: ConversationNotificationAction[]

  /**
   * Some notifications (like price adjustments) may include an extra object.
   */
  priceAdjustement?: ConversationNotificationPriceAdjustement
}

/**
 * Details for adjusting a price in a conversation notification.
 */
export interface ConversationNotificationPriceAdjustement {
  '@type': 'ConversationNotificationPriceAdjustement'
  '@id': string
  before: string
  after: string
}

/**
 * A reaction (emoji, etc.) on a message.
 */
export interface Reaction {
  '@type': 'Reaction'
  '@id': string
  uuid: string
  author: string
  emoji: string
  fromCurrentUser: boolean
}

/**
 * Actions that can appear in ConversationNotification or TransactionSummary.
 */
export interface ConversationNotificationAction {
  '@type': 'ConversationNotificationAction'
  '@id': string
  style: string // e.g., "primary", "secondary", etc.
  label: string
  target: string // e.g., "/reservations/demo1/accept"
  type: string // e.g., "hook"
  hook: string // e.g., "useAcceptReservation"
}

/**
 * TransactionSummary
 */
export interface TransactionSummary {
  '@type': 'TransactionSummary'
  '@id': string
  reservationUuid: string
  invoice: TransactionSummaryCartInvoice
  fees: TransactionSummaryCartFee[]
  totalFee: number
  totalTTC: number
  cartHeader: PlainWidthBadge
  services: PlainWidthBadge[]
  statusInfo: string
  actions: ConversationNotificationAction[]
}

/**
 * Represents the invoice part of the transaction summary.
 */
export interface TransactionSummaryCartInvoice {
  '@type': 'TransactionSummaryCartInvoice'
  '@id': string
  items: TransactionSummaryCartItem[]
  total: TransactionSummaryCartInvoiceTotal
}

/**
 * An item in the invoice.
 */
export interface TransactionSummaryCartItem {
  '@type': 'TransactionSummaryCartItem'
  '@id': string
  title: string
  description: string
  value: number
}

/**
 * Total details for the invoice.
 */
export interface TransactionSummaryCartInvoiceTotal {
  '@type': 'TransactionSummaryCartInvoiceTotal'
  '@id': string
  total: number
  adjustment: number
  discountedTotal: number
}

/**
 * Represents a fee in the transaction summary.
 */
export interface TransactionSummaryCartFee {
  '@type': 'TransactionSummaryCartFee'
  '@id': string
  title: string
  description: string
  value: number
}

/**
 * Header details for a conversation.
 */
export interface ConversationHeader {
  '@type': string
  '@id': string
  uuid: string
  title: string
  reservationUuid: string
  authorAvatarUrl: string
  conversationUuid: string
}

/**
 * A "badge" object that displays certain info, e.g., “Garde chez le propriétaire”.
 */
export interface PlainWidthBadge {
  '@type': 'PlainWidthBadge'
  '@id': string
  title: string
  description?: string
  type?: string
  icon?: BadgeIcon
  plainWidth: boolean
}

/**
 * Icon details for a badge (e.g., type = "emt-home").
 */
export interface BadgeIcon {
  '@type': 'BadgeIcon'
  '@id': string
  type: string
}

/**
 * Reservation quote
 */
export interface Quote {
  '@context': string
  '@id': string
  '@type': 'Quote'
  '@accept'?: IRI
  '@reject'?: IRI
  '@cancel'?: IRI
  '@payment_intent'?: IRI
  id: string
  order: Order
  status: string
  petsitterId: string
  customerId: string
  animals: string[]
}

export type MissionQuote = Omit<Quote, 'order' | 'animals'> &
  Partial<Omit<Order, '@type'>> & {
    order?: Order
    animals?: any[]
  }

export type MissionsResponse = HydraCollection<MissionQuote>

export interface Order {
  '@type': 'Order'
  '@id': string
  cartHeader: PlainWidthBadge
  products: OrderItem[]
  multipleAnimalsFactor: OrderItem
  adjustment: OrderItem
  defaultSecurityFee: OrderItem
  adjustmentPercent: number
  securityFees: OrderItem
  commission: OrderItem
  isCustomerPremium: boolean
  total: number
  orderDetails: OrderDetailsItem[]
  cancellationPolicies: string
}

export interface OrderItem {
  '@type': 'OrderItem'
  '@id': string
  title: string
  price: number
  summary: string[]
}

export interface OrderDetailsItem {
  '@type': 'OrderDetailsItem'
  '@id': string
  productSlug: string
  periods: Period[]
  operationType: string
}

export interface Period {
  start: string
  end: string
}

export const transformStatus = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    DRAFT: 'pending',
    OPENED: 'pending',
    PENDING_AUTHORIZATION: 'pending',
    AUTHORIZATION_FAILLED: 'pending',
    CANCELLED: 'cancelled',
    REFUNDED: 'cancelled',
    REJECTED: 'refused',
    APPROVED: 'accepted',
    STARTED: 'inprogress',
    IN_PROGRESS: 'inprogress',
    PAID: 'reserved',
    CONFIRMED: 'reserved',
    AWARDED: 'reserved',
    COMPLETED: 'completed',
    FINALIZED: 'over',
    DISPUTED: 'disputed',
    CLOSED: 'expired',
    EXPIRED: 'expired',
    ARCHIVED: 'expired',
  }

  return statusMap[status] || status
}
